// Client-side telemetry — Sentry HTTP ingest, zero dependencies.
//
// Wires `captureException` / `captureEvent` into a thin wrapper that:
//   - In dev (no DSN set): logs to console only.
//   - In prod with VITE_SENTRY_DSN set: fire-and-forget POST to Sentry's
//     `/api/<project_id>/envelope/` ingest endpoint.
//
// We intentionally do NOT pull `@sentry/browser` into the bundle — the
// SDK is heavy and most of its surface (breadcrumbs, sourcemap upload,
// session replay) is overkill for this app. When/if you outgrow this,
// swap captureException/captureEvent for the SDK calls and remove the
// manual envelope builder.
//
// Setup: in Cloudflare Pages dashboard → Settings → Environment variables
// (Production), add `VITE_SENTRY_DSN` with the project DSN
// (https://<publicKey>@<host>/<projectId>). Without that var the helpers
// are inert.

interface ParsedDsn {
  host: string;
  projectId: string;
  publicKey: string;
}

let dsn: ParsedDsn | null = null;
let initialized = false;

// Latest Core Web Vitals readings, kept here so captureException can
// surface them as tags on the error envelope. Sentry groups/filter on
// tag values — having LCP/INP/CLS attached to every error makes
// "errors that fired on slow loads" queryable in one click. Populated
// by setWebVitalTag (called from src/lib/cwv.ts as each metric fires).
const webVitalTags: Record<string, number> = {};

/**
 * Record the most recent reading of a Core Web Vital. Called from
 * src/lib/cwv.ts's onMetric. Values are rounded to integers (Sentry
 * tag values must be short strings — millisecond precision is
 * sufficient for triage).
 */
export function setWebVitalTag(name: string, value: number): void {
  webVitalTags[`cwv.${name.toLowerCase()}`] = Math.round(value);
}

function parseDsn(raw: string): ParsedDsn | null {
  try {
    const u = new URL(raw);
    const projectId = u.pathname.replace(/^\//, '');
    if (!projectId || !u.username || !u.host) return null;
    return { host: u.host, projectId, publicKey: u.username };
  } catch {
    return null;
  }
}

/**
 * Initialize telemetry. Idempotent. Call once from main.tsx after the SW
 * init. Reads `import.meta.env.VITE_SENTRY_DSN`; no-op if absent or malformed.
 */
export function initTelemetry(): void {
  if (initialized) return;
  initialized = true;
  const raw = (import.meta.env.VITE_SENTRY_DSN ?? '').trim();
  if (!raw) {
    dsn = null;
    return;
  }
  dsn = parseDsn(raw);
  if (!dsn && import.meta.env.DEV) {
    console.warn('[telemetry] VITE_SENTRY_DSN is set but failed to parse — events will not ship');
  }
}

function buildEnvelope(payload: {
  level: 'error' | 'info';
  message: string;
  extra?: Record<string, unknown>;
  err?: { name: string; message: string; stack?: string };
}): string | null {
  if (!dsn) return null;
  const eventId = crypto.randomUUID().replace(/-/g, '');
  const ts = new Date().toISOString();
  const release = (import.meta.env.VITE_APP_VERSION ?? '').toString() || undefined;

  // Tags: include the most recent CWV readings as searchable values
  // alongside the base 'runtime' tag. Sentry accepts only string tag
  // values, so we coerce. If no CWV has fired yet (e.g. the error
  // happens during initial hydration before LCP), the tag is just
  // omitted — Sentry handles missing tags fine.
  const cwvTags: Record<string, string> = {};
  for (const [k, v] of Object.entries(webVitalTags)) {
    cwvTags[k] = String(v);
  }

  const event = {
    event_id: eventId,
    timestamp: ts,
    platform: 'javascript',
    level: payload.level,
    logger: 'web-client',
    message: payload.message,
    release,
    environment: import.meta.env.DEV ? 'development' : 'production',
    tags: { runtime: 'browser', ...cwvTags },
    extra: payload.extra ?? {},
    exception: payload.err
      ? {
          values: [
            {
              type: payload.err.name,
              value: payload.err.message,
              stacktrace: payload.err.stack ? { frames: [] } : undefined,
            },
          ],
        }
      : undefined,
  };

  // Sentry envelope = newline-separated header lines.
  const header = JSON.stringify({ event_id: eventId, sent_at: ts, dsn: undefined });
  const itemHeader = JSON.stringify({ type: 'event' });
  const itemBody = JSON.stringify(event);
  return `${header}\n${itemHeader}\n${itemBody}\n`;
}

function shipEnvelope(body: string): void {
  if (!dsn) return;
  const url = `https://${dsn.host}/api/${dsn.projectId}/envelope/?sentry_key=${dsn.publicKey}&sentry_version=7`;
  // Fire-and-forget. We never await this and any failure is swallowed —
  // observability must not break the app.
  try {
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-sentry-envelope' },
      body,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {
      // ignore
    });
  } catch {
    // ignore
  }
}

/**
 * Report an exception. Safe to call before initTelemetry() — events drop
 * silently (dev: also console.error) if no DSN is configured.
 */
export function captureException(
  err: unknown,
  extra?: Record<string, unknown>,
): void {
  const errObj =
    err instanceof Error
      ? { name: err.name, message: err.message, stack: err.stack }
      : { name: 'NonError', message: String(err) };

  if (!dsn) {
    if (import.meta.env.DEV) {
      console.error('[telemetry] captureException (no DSN):', errObj.message, extra ?? '');
    }
    return;
  }

  const envelope = buildEnvelope({
    level: 'error',
    message: errObj.message,
    extra,
    err: errObj,
  });
  if (envelope) shipEnvelope(envelope);
}

